import { Link } from 'react-router-dom';
import { Home } from 'lucide-react';

function MultiFamily() {
  return (
    <div className="pt-28">
      {/* Hero Section with Background Image */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
        ></div>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Home className="w-16 h-16 text-gold mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Multi-Family <span className="text-gold">Residential</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Apartments, condominiums, townhomes, and mixed-use residential developments
          </p>
        </div>
      </section>

      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-gray-300 space-y-6 text-lg leading-relaxed">
            <p>
              Multi-family residential construction demands a unique blend of efficiency, quality, and attention to detail. Williams Diversified brings decades of experience in delivering apartment complexes, condominium developments, townhome communities, and mixed-use residential projects on time and within budget.
            </p>
            <p>
              We understand the complexities of multi-family construction—from managing multiple unit types and common areas to coordinating with property managers and meeting occupancy deadlines. Our team excels at delivering comfortable, durable living spaces that residents are proud to call home.
            </p>
            <p>
              Whether you're developing a luxury high-rise, an affordable housing community, or a student housing facility, we have the expertise and resources to bring your vision to life.
            </p>
            
            <h3 className="text-3xl font-bold text-white mt-12 mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>Our Capabilities</h3>
            <ul className="space-y-4">
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Apartment complex construction (garden-style, mid-rise, high-rise)</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Condominium development and construction</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Townhome and attached housing communities</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Mixed-use residential with retail/commercial</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Student housing and senior living facilities</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Affordable housing development</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Common area and amenity construction</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Renovation and repositioning of existing properties</span></li>
            </ul>

            <h3 className="text-3xl font-bold text-white mt-12 mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>Why Choose Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Experienced in all multi-family construction types</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Strong relationships with subcontractors and suppliers</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Phased occupancy and turnover expertise</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Value engineering to maximize ROI</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Quality construction that minimizes maintenance costs</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Facility Maintenance Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-8 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Multi-Family <span className="text-gold">Facility Maintenance</span>
          </h3>
          <p className="text-gray-300 text-lg leading-relaxed text-center mb-8">
            Keep your residential properties in top condition with our comprehensive maintenance services designed for apartment complexes and multi-family communities.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6">
              <h4 className="text-gold font-bold text-xl mb-3">Preventive Maintenance</h4>
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Common area HVAC & mechanical systems</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Pool, fitness center & amenity upkeep</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Parking lot & exterior lighting</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Roof & building envelope inspections</li>
              </ul>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6">
              <h4 className="text-gold font-bold text-xl mb-3">24/7 Emergency Response</h4>
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Plumbing leaks & water damage</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Heating & cooling failures</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Elevator & access system repairs</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Storm damage & emergency repairs</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* National Rollout Programs Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-4 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            National <span className="text-gold">Rollout Programs</span>
          </h3>
          <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">
            Scalable construction solutions for multi-family developers requiring consistent quality across multiple communities nationwide
          </p>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">50+</div>
              <div className="text-gray-300 font-medium mb-2">States Covered</div>
              <p className="text-gray-500 text-sm">Nationwide service capability with local expertise</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">1</div>
              <div className="text-gray-300 font-medium mb-2">Point of Contact</div>
              <p className="text-gray-500 text-sm">Single account manager for all properties</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">100%</div>
              <div className="text-gray-300 font-medium mb-2">Quality Consistency</div>
              <p className="text-gray-500 text-sm">Standardized execution across every community</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">24/7</div>
              <div className="text-gray-300 font-medium mb-2">Support</div>
              <p className="text-gray-500 text-sm">Round-the-clock emergency response</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-4 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Why Choose Us for <span className="text-gold">Multi-Family</span>
          </h3>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Creating communities where residents are proud to call home
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">01</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Lease-Up Ready</h4>
                <p className="text-gray-400 text-sm mb-4">On-time delivery is critical for lease-up success. We hit occupancy dates so your revenue projections stay on track.</p>
              </div>
            </div>
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">02</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Quality That Lasts</h4>
                <p className="text-gray-400 text-sm mb-4">Durable construction that minimizes maintenance costs. Building quality reduces callbacks and keeps residents happy long-term.</p>
              </div>
            </div>
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">03</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Amenity Expertise</h4>
                <p className="text-gray-400 text-sm mb-4">From pools to fitness centers to clubhouses, we build the amenities that differentiate your property and attract residents.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-12 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Our Multi-Family <span className="text-gold">Construction Process</span>
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">1</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Pro Forma Review</h4>
              <p className="text-gray-400 text-sm">Budget alignment and value engineering to maximize your return while meeting quality standards.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">2</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Phased Construction</h4>
              <p className="text-gray-400 text-sm">Building-by-building scheduling to enable early lease-up while construction continues.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">3</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Quality Build</h4>
              <p className="text-gray-400 text-sm">Durable construction with quality inspections to minimize callbacks and maintenance costs.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">4</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Lease-Up Ready</h4>
              <p className="text-gray-400 text-sm">Unit turnover, amenity completion, and model unit preparation for successful lease-up.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Ready to Build Your Next <span className="text-gold">Multi-Family Project</span>?
          </h3>
          <p className="text-gray-300 text-lg mb-8">
            Contact us today to discuss your residential development needs.
          </p>
          <Link to="/contact" className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider">Contact Us</Link>
        </div>
      </section>
    </div>
  );
}

export default MultiFamily;
